/* Sprint 1 (FLY2619) , Case Status Portal (external).
   Reporter (named or anonymous) enters Case Number + Secure Verification Code,
   sees high-level stages only + can send plain-text messages to the assigned
   investigator. NO internal notes, evidence, assignees, or AI content exposed.
*/
import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  Search, Send, ArrowLeft, ShieldCheck, MessageSquare, Clock, CheckCircle2, Upload, Paperclip, FileText, User as UserIcon,
} from "lucide-react";
import Logo from "../components/Logo";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STAGES = ["In Progress", "Awaiting Response", "Findings Drafted", "Closed"];

export default function IntakeStatus() {
  const [params, setParams] = useSearchParams();
  const [caseNumber, setCaseNumber] = useState((params.get("case") || "").toUpperCase());
  const [code, setCode] = useState("");
  const [data, setData] = useState(null);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [evType, setEvType] = useState("document");
  const [evDesc, setEvDesc] = useState("");
  const [evBusy, setEvBusy] = useState(false);
  const fileRef = useRef(null);

  const load = async (cn, c) => {
    setBusy(true);
    try {
      const { data: d } = await axios.get(`${API_BASE}/intake/status/${cn}`, { params: { code: c } });
      setData(d);
      setParams({ case: cn }, { replace: true });
    } catch (e) {
      const detail = e?.response?.data?.detail || "Case not found or code invalid.";
      toast.error(String(detail));
      setData(null);
    } finally {
      setBusy(false);
    }
  };

  const sendMessage = async () => {
    if (!msg.trim() || !data) return;
    setBusy(true);
    try {
      await axios.post(`${API_BASE}/intake/status/${data.case_number}/messages`, {
        code,
        message: msg.trim(),
      });
      toast.success("Message delivered to the investigator");
      setMsg("");
      await load(data.case_number, code);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send message");
    } finally {
      setBusy(false);
    }
  };

  const uploadEvidence = async (file) => {
    if (!file || !data) return;
    setEvBusy(true);
    try {
      const fd = new FormData();
      fd.append("code", code);
      fd.append("evidence_type", evType);
      if (evDesc.trim()) fd.append("description", evDesc.trim());
      fd.append("file", file);
      await axios.post(
        `${API_BASE}/intake/status/${data.case_number}/evidence`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      toast.success(`Uploaded ${file.name} , investigator notified`);
      setEvDesc("");
      if (fileRef.current) fileRef.current.value = "";
      await load(data.case_number, code);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setEvBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-amber-50">
      <header className="border-b border-slate-200/70 bg-white/70 backdrop-blur">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Logo variant="dark" size={28} />
            <div className="text-xs font-mono uppercase tracking-widest text-slate-500 border-l border-slate-200 pl-3">
              Case Status Portal
            </div>
          </div>
          <Link to="/intake/acme" className="text-sm text-indigo-700 font-medium hover:underline flex items-center gap-1">
            <ArrowLeft size={14} strokeWidth={2} /> New report
          </Link>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 py-10">
        {!data ? (
          <motion.section initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="max-w-lg mx-auto rounded-xl border border-slate-200 bg-white shadow-lg p-8">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-100 text-indigo-800 text-xs font-mono uppercase tracking-widest mb-3">
              <ShieldCheck size={12} strokeWidth={2.5} /> Encrypted lookup
            </div>
            <h1 className="text-3xl font-semibold tracking-tight">Track your report</h1>
            <p className="mt-2 text-slate-600 text-sm">
              Enter your Case Number and the Secure Verification Code from your submission receipt.
            </p>

            <div className="mt-6 space-y-4">
              <div>
                <label className="text-[11px] font-mono uppercase tracking-widest text-slate-500">Case Number</label>
                <input
                  value={caseNumber}
                  onChange={(e) => setCaseNumber(e.target.value.toUpperCase())}
                  placeholder="PHR-2026-0001"
                  className="mt-1 w-full font-mono border border-slate-300 rounded px-3 py-2 focus:outline-none focus:border-indigo-500"
                  data-testid="portal-case-number"
                />
              </div>
              <div>
                <label className="text-[11px] font-mono uppercase tracking-widest text-slate-500">Secure Verification Code</label>
                <input
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  placeholder="XXXX-XXXX"
                  className="mt-1 w-full font-mono border border-slate-300 rounded px-3 py-2 focus:outline-none focus:border-indigo-500"
                  data-testid="portal-code"
                />
              </div>
              <button
                onClick={() => load(caseNumber.trim(), code.trim())}
                disabled={busy || !caseNumber.trim() || !code.trim()}
                className={"w-full py-2.5 rounded font-medium inline-flex items-center justify-center gap-2 " +
                  ((busy || !caseNumber.trim() || !code.trim())
                    ? "bg-slate-300 text-slate-500 cursor-not-allowed"
                    : "bg-indigo-700 text-white hover:bg-indigo-800")}
                data-testid="portal-lookup"
              >
                <Search size={14} /> {busy ? "Looking up..." : "Look up my case"}
              </button>
            </div>
          </motion.section>
        ) : (
          <motion.section initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="space-y-6" data-testid="portal-view">
            <div className="rounded-xl border border-slate-200 bg-white p-6">
              <div className="flex items-start justify-between flex-wrap gap-4">
                <div>
                  <div className="text-[11px] font-mono uppercase tracking-widest text-slate-500">Case Number</div>
                  <div className="text-2xl font-mono font-semibold" data-testid="portal-case-title">{data.case_number}</div>
                  <div className="mt-1 text-slate-700">{data.title}</div>
                  {data.is_anonymous && (
                    <div className="mt-2 inline-flex items-center gap-1 text-xs bg-amber-100 text-amber-900 rounded-full px-2 py-0.5">
                      <ShieldCheck size={11} /> Anonymous report
                    </div>
                  )}
                </div>
                <StageChip stage={data.stage} />
              </div>
            </div>

            <div className="rounded-xl border border-slate-200 bg-white p-6">
              <div className="text-[11px] font-mono uppercase tracking-widest text-slate-500 mb-3">Progress</div>
              <div className="flex items-center gap-2">
                {STAGES.map((s, i) => (
                  <div key={s} className="flex-1">
                    <div className={"h-1.5 rounded " +
                      (STAGES.indexOf(data.stage) >= i ? "bg-indigo-700" : "bg-slate-200")} />
                    <div className={"mt-1.5 text-[10px] font-mono uppercase tracking-widest " +
                      (STAGES.indexOf(data.stage) >= i ? "text-indigo-800" : "text-slate-400")}>{s}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-xl border border-slate-200 bg-white p-6">
              <div className="text-[11px] font-mono uppercase tracking-widest text-slate-500 mb-3">Milestones</div>
              {(data.milestones || []).length === 0 ? (
                <div className="text-sm text-slate-500">No public milestones yet. Your investigator is reviewing the case.</div>
              ) : (
                <ol className="space-y-3">
                  {data.milestones.map((m, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <div className="mt-1 w-2 h-2 rounded-full bg-indigo-700" />
                      <div>
                        <div className="text-sm text-slate-800">{m.message}</div>
                        <div className="text-[11px] font-mono text-slate-500">{new Date(m.created_at).toLocaleString()}</div>
                      </div>
                    </li>
                  ))}
                </ol>
              )}
            </div>

            <div className="rounded-xl border border-slate-200 bg-white p-6">
              <div className="flex items-center gap-2 mb-3">
                <MessageSquare size={14} className="text-indigo-700" />
                <div className="text-[11px] font-mono uppercase tracking-widest text-slate-500">Send a message to your investigator</div>
              </div>
              <textarea
                rows={4}
                value={msg}
                onChange={(e) => setMsg(e.target.value)}
                placeholder="You can share extra context, ask a question, or add corrections. This message is added to the case audit trail."
                className="w-full border border-slate-300 rounded px-3 py-2 focus:outline-none focus:border-indigo-500"
                data-testid="portal-message-input"
              />
              <div className="mt-2 flex justify-end">
                <button
                  onClick={sendMessage}
                  disabled={busy || !msg.trim()}
                  className={"px-4 py-2 rounded inline-flex items-center gap-2 text-sm font-medium " +
                    ((busy || !msg.trim())
                      ? "bg-slate-300 text-slate-500 cursor-not-allowed"
                      : "bg-indigo-700 text-white hover:bg-indigo-800")}
                  data-testid="portal-message-send"
                >
                  <Send size={14} /> {busy ? "Sending..." : "Send message"}
                </button>
              </div>
              {(data.messages || []).length > 0 && (
                <div className="mt-5 space-y-3">
                  <div className="text-[11px] font-mono uppercase tracking-widest text-slate-500">Conversation</div>
                  {data.messages.map((m, i) => (
                    <div key={i} className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                      <div className="flex items-center gap-2 text-[11px] font-mono text-slate-500">
                        <CheckCircle2 size={11} className="text-emerald-600" />
                        {m.author} <span className="text-slate-400">·</span>
                        {new Date(m.created_at).toLocaleString()}
                      </div>
                      <div className="mt-1 text-sm text-slate-800 whitespace-pre-wrap">{m.message}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Evidence upload widget , only while the case is still open. */}
            <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="portal-evidence-block">
              <div className="flex items-center gap-2 mb-3">
                <Upload size={14} className="text-indigo-700" />
                <div className="text-[11px] font-mono uppercase tracking-widest text-slate-500">Add evidence</div>
              </div>
              {data.can_upload_evidence === false ? (
                <div className="text-sm text-slate-500 italic">
                  This case is closed. Evidence uploads are no longer accepted.
                </div>
              ) : (
                <>
                  <div className="grid md:grid-cols-3 gap-3 mb-3">
                    {[
                      { key: "statement", label: "Statement", icon: UserIcon, hint: "A written account or transcript" },
                      { key: "document", label: "Document", icon: FileText, hint: "PDFs, screenshots, emails" },
                      { key: "other", label: "Other", icon: Paperclip, hint: "Photos, audio, anything else" },
                    ].map(({ key, label, icon: Icon, hint }) => (
                      <button
                        key={key}
                        onClick={() => setEvType(key)}
                        className={"text-left border rounded-lg p-3 transition " +
                          (evType === key
                            ? "border-indigo-600 bg-indigo-50"
                            : "border-slate-200 hover:border-slate-400")}
                        data-testid={`portal-evtype-${key}`}
                      >
                        <div className="flex items-center gap-2 text-sm font-semibold text-slate-900 mb-0.5">
                          <Icon size={14} className={evType === key ? "text-indigo-700" : "text-slate-400"} strokeWidth={2} />
                          {label}
                        </div>
                        <div className="text-[11px] text-slate-500">{hint}</div>
                      </button>
                    ))}
                  </div>
                  <textarea
                    rows={2}
                    value={evDesc}
                    onChange={(e) => setEvDesc(e.target.value)}
                    placeholder="Short description (optional): what is this evidence and how does it relate to your case?"
                    className="w-full border border-slate-300 rounded px-3 py-2 focus:outline-none focus:border-indigo-500 mb-3"
                    data-testid="portal-ev-desc"
                  />
                  <div className="flex items-center gap-2">
                    <input
                      ref={fileRef}
                      type="file"
                      className="hidden"
                      onChange={(e) => uploadEvidence(e.target.files?.[0])}
                      data-testid="portal-ev-file"
                    />
                    <button
                      onClick={() => fileRef.current?.click()}
                      disabled={evBusy}
                      className={"px-4 py-2 rounded inline-flex items-center gap-2 text-sm font-medium " +
                        (evBusy
                          ? "bg-slate-300 text-slate-500 cursor-not-allowed"
                          : "bg-indigo-700 text-white hover:bg-indigo-800")}
                      data-testid="portal-ev-upload"
                    >
                      <Upload size={14} /> {evBusy ? "Uploading..." : "Choose file and upload"}
                    </button>
                    <span className="text-[11px] font-mono text-slate-500">Max 25 MB per file</span>
                  </div>
                </>
              )}

              {(data.my_attachments || []).length > 0 && (
                <div className="mt-5">
                  <div className="text-[11px] font-mono uppercase tracking-widest text-slate-500 mb-2">Evidence you have submitted</div>
                  <ul className="space-y-2">
                    {data.my_attachments.map((a) => (
                      <li key={a.id} className="border border-slate-200 rounded p-3 flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="text-sm font-medium truncate">{a.filename}</div>
                          <div className="text-[11px] font-mono text-slate-500 mt-0.5">
                            {(a.evidence_type || "other").toUpperCase()} · {Math.round((a.size || 0) / 1024)} KB
                            {a.created_at && <> · {new Date(a.created_at).toLocaleDateString()}</>}
                          </div>
                          {a.reason && <div className="text-[11px] italic text-slate-600 mt-1">&ldquo;{a.reason}&rdquo;</div>}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </motion.section>
        )}
      </div>
    </div>
  );
}

function StageChip({ stage }) {
  const color = {
    "In Progress": "bg-amber-100 text-amber-900",
    "Awaiting Response": "bg-blue-100 text-blue-900",
    "Findings Drafted": "bg-indigo-100 text-indigo-900",
    "Closed": "bg-emerald-100 text-emerald-900",
  }[stage] || "bg-slate-100 text-slate-800";
  return (
    <div className={"inline-flex items-center gap-2 rounded-full px-3 py-1 text-sm font-medium " + color} data-testid="portal-stage">
      <Clock size={12} /> {stage}
    </div>
  );
}
